package com.tovtalk.presentation.viewmodel.auth

import com.tovtalk.domain.entity.LoginMethod
import com.tovtalk.domain.entity.DomainResult
import com.tovtalk.domain.entity.User
import com.tovtalk.domain.repository.SettingsRepository
import com.tovtalk.domain.repository.UserRepository
import com.tovtalk.domain.usecase.auth.LoginUseCase
import com.tovtalk.domain.usecase.auth.RegisterUseCase
import com.tovtalk.presentation.state.UiState
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch

import com.tovtalk.presentation.ViewModel

/**
 * Auth ViewModel
 */
class AuthViewModel(
    private val loginUseCase: LoginUseCase,
    private val registerUseCase: RegisterUseCase,
    private val userRepository: UserRepository,
    private val settingsRepository: SettingsRepository,
    private val scope: CoroutineScope
) : ViewModel() {
    private val _loginState = MutableStateFlow<UiState<User>>(UiState.Idle)
    val loginState: StateFlow<UiState<User>> = _loginState.asStateFlow()
    
    private val _registerState = MutableStateFlow<UiState<User>>(UiState.Idle)
    val registerState: StateFlow<UiState<User>> = _registerState.asStateFlow()

    private val _guestContinueState = MutableStateFlow<UiState<Unit>>(UiState.Idle)
    val guestContinueState: StateFlow<UiState<Unit>> = _guestContinueState.asStateFlow()
    
    fun login(identity: String, password: String?, loginMethod: LoginMethod, googleAccessToken: String? = null) {
        scope.launch {
            _loginState.value = UiState.Loading
            com.tovtalk.utils.logger.Logger.d("Attempting login method=$loginMethod", "AuthViewModel")

            val kosherLevelId = settingsRepository.getOnboardingKashrutLevel()
            when (val result = loginUseCase(identity, password, loginMethod, googleAccessToken, kosherLevelId = kosherLevelId)) {
                is DomainResult.Success -> {
                    com.tovtalk.utils.logger.Logger.d("Login success", "AuthViewModel")
                    _loginState.value = UiState.Success(result.data)

                    // Sync language and onboarding preferences to server after successful login
                    syncLanguageToServer()
                    syncOnboardingPreferencesToServer()
                }
                is DomainResult.Error -> {
                    com.tovtalk.utils.logger.Logger.e("Login failed: ${result.message}", "AuthViewModel")
                    result.cause?.printStackTrace()
                    _loginState.value = UiState.Error(result.message)
                }
                is DomainResult.Loading -> {
                     com.tovtalk.utils.logger.Logger.d("Login loading", "AuthViewModel")
                    _loginState.value = UiState.Loading
                }
            }
        }
    }

    /**
     * Complete login with a Firebase token (e.g. after Google redirect on Web).
     */
    fun loginWithFirebaseToken(firebaseToken: String) {
        com.tovtalk.utils.logger.Logger.d("GoogleRedirect: AuthViewModel loginWithFirebaseToken ENTER tokenLen=${firebaseToken.length}", "GoogleRedirect")
        scope.launch {
            _loginState.value = UiState.Loading
            val kosherLevelId = settingsRepository.getOnboardingKashrutLevel()
            when (val result = userRepository.login(firebaseToken, null, LoginMethod.GOOGLE, com.tovtalk.utils.config.isHadranDevice(), kosherLevelId)) {
                is DomainResult.Success -> {
                    com.tovtalk.utils.logger.Logger.d("GoogleRedirect: AuthViewModel loginWithFirebaseToken SUCCESS userId=${result.data.id}", "GoogleRedirect")
                    _loginState.value = UiState.Success(result.data)
                    syncLanguageToServer()
                    syncOnboardingPreferencesToServer()
                }
                is DomainResult.Error -> {
                    com.tovtalk.utils.logger.Logger.e("GoogleRedirect: AuthViewModel loginWithFirebaseToken ERROR ${result.message}", "GoogleRedirect")
                    _loginState.value = UiState.Error(result.message)
                }
                else -> {}
            }
        }
    }
    
    /**
     * Sync local language preference to server
     */
    private suspend fun syncLanguageToServer() {
        try {
            val localLanguage = settingsRepository.getAppLanguage()
            val updateResult = userRepository.updateUserSettings(appLanguage = localLanguage)
            when (updateResult) {
                is DomainResult.Success -> {
                    com.tovtalk.utils.logger.Logger.d("Language synced to server", "AuthViewModel")
                }
                is DomainResult.Error -> {
                    com.tovtalk.utils.logger.Logger.e("Failed to sync language: ${updateResult.message}", "AuthViewModel")
                }
                else -> {}
            }
        } catch (e: Exception) {
            com.tovtalk.utils.logger.Logger.e("Error syncing language: ${e.message}", "AuthViewModel", e)
        }
    }
    
    /**
     * Sync onboarding kashrut & category preferences to server after login/register
     */
    private suspend fun syncOnboardingPreferencesToServer() {
        try {
            val categoryIds = settingsRepository.getOnboardingConsumptionCategoryIds()
            if (categoryIds.isNotEmpty()) {
                val updateResult = userRepository.updateProfile(
                    mapOf("consumption_category_ids" to categoryIds)
                )
                when (updateResult) {
                    is DomainResult.Success -> {
                        com.tovtalk.utils.logger.Logger.d("Onboarding preferences synced to server", "AuthViewModel")
                    }
                    is DomainResult.Error -> {
                        com.tovtalk.utils.logger.Logger.e("Failed to sync onboarding preferences: ${updateResult.message}", "AuthViewModel")
                    }
                    else -> {}
                }
            }
        } catch (e: Exception) {
            com.tovtalk.utils.logger.Logger.e("Error syncing onboarding preferences: ${e.message}", "AuthViewModel", e)
        }
    }

    fun register(fullname: String, email: String, password: String, loginMethod: LoginMethod) {
        scope.launch {
            _registerState.value = UiState.Loading
            
            when (val result = registerUseCase(fullname, email, password, loginMethod)) {
                is DomainResult.Success -> {
                    _registerState.value = UiState.Success(result.data)

                    // Sync language and onboarding preferences to server after successful registration
                    syncLanguageToServer()
                    syncOnboardingPreferencesToServer()
                }
                is DomainResult.Error -> {
                    _registerState.value = UiState.Error(result.message)
                }
                is DomainResult.Loading -> {
                    _registerState.value = UiState.Loading
                }
            }
        }
    }
    
    fun continueAsGuest() {
        scope.launch {
            _guestContinueState.value = UiState.Loading
            val kosherLevelId = settingsRepository.getOnboardingKashrutLevel()
            val consumptionCategoryIds = settingsRepository.getOnboardingConsumptionCategoryIds()
            when (val result = userRepository.continueAsGuest(kosherLevelId, consumptionCategoryIds.ifEmpty { null })) {
                is DomainResult.Success -> {
                    _guestContinueState.value = UiState.Success(Unit)
                }
                is DomainResult.Error -> {
                    _guestContinueState.value = UiState.Error(result.message)
                }
                else -> {}
            }
        }
    }

    fun resetGuestContinueState() {
        _guestContinueState.value = UiState.Idle
    }
    
    fun resetLoginState() {
        _loginState.value = UiState.Idle
    }
    
    fun resetRegisterState() {
        _registerState.value = UiState.Idle
    }
}
